import { useEffect, useRef, useState } from 'react';
import { PortfolioItem } from '@/hooks/profile';
import { PortfolioService } from '@/services/supabase/portfolio';
import { useAuth } from '@/hooks/domains/auth/useAuth';
import { useQueryClient } from '@tanstack/react-query';

export type SaveStatus = 'idle' | 'saving' | 'saved' | 'error';

interface UsePortfolioAutosaveOptions {
  portfolioItem: Partial<PortfolioItem>;
  itemId?: string;
  debounceMs?: number;
  enabled?: boolean;
  isManualSaving?: boolean; // Disable autosave when manual save is in progress
  onSaveSuccess?: (savedItem: PortfolioItem) => void;
  onSaveError?: (error: Error) => void;
}

/**
 * Custom hook for silent autosave of portfolio items
 *
 * Features:
 * - Debounced saves (default 2 seconds after last change)
 * - Auto-creates draft on first meaningful change
 * - Updates existing drafts silently
 * - Returns save status for subtle UI indicators
 * - No toast notifications (except errors)
 */
export function usePortfolioAutosave({
  portfolioItem,
  itemId,
  debounceMs = 2000,
  enabled = true,
  isManualSaving = false,
  onSaveSuccess,
  onSaveError,
}: UsePortfolioAutosaveOptions) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [saveStatus, setSaveStatus] = useState<SaveStatus>('idle');
  const [currentItemId, setCurrentItemId] = useState<string | undefined>(itemId);
  const hasCreatedRef = useRef(false);
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastSavedDataRef = useRef<string>('');

  // Check if portfolio item has meaningful content
  const hasMeaningfulContent = () => {
    return !!(
      portfolioItem.title?.trim() ||
      portfolioItem.description?.trim() ||
      portfolioItem.image_url ||
      portfolioItem.images?.length
    );
  };

  // Serialize portfolio item for comparison
  const serializePortfolioItem = (item: Partial<PortfolioItem>) => {
    return JSON.stringify({
      title: item.title || '',
      description: item.description || '',
      image_url: item.image_url || '',
      images: item.images || [],
      status: item.status || 'draft',
      is_featured: item.is_featured || false,
    });
  };

  // Keep current item id in sync with external changes
  useEffect(() => {
    setCurrentItemId(itemId);

    if (!itemId) {
      hasCreatedRef.current = false;
    }
  }, [itemId]);

  // Clear pending autosave when disabled
  useEffect(() => {
    if (!enabled && saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
      saveTimeoutRef.current = null;
      setSaveStatus('idle');
    }
  }, [enabled]);

  // Autosave effect
  useEffect(() => {
    // Don't autosave if no user or no meaningful content or manual save is in progress
    if (!enabled || !user || !hasMeaningfulContent() || isManualSaving) {
      return;
    }

    // Clear existing timeout
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    // Set new timeout for debounced save
    saveTimeoutRef.current = setTimeout(async () => {
      try {
        // Serialize current data INSIDE setTimeout to capture freshest state
        // This prevents race condition where user continues typing after serialization
        const currentData = serializePortfolioItem(portfolioItem);

        // Don't save if data hasn't changed
        if (currentData === lastSavedDataRef.current) {
          return;
        }

        setSaveStatus('saving');
        console.log('[usePortfolioAutosave] Auto-saving portfolio item...');

        let savedItem: PortfolioItem;

        if (currentItemId) {
          try {
            // Update existing item
            savedItem = await PortfolioService.updatePortfolioItem(currentItemId, {
              id: currentItemId,
              ...portfolioItem,
              status: portfolioItem.status || 'draft',
            });
            console.log('[usePortfolioAutosave] Updated existing item:', savedItem.id);
          } catch (updateError) {
            if (updateError instanceof Error && updateError.message.toLowerCase().includes('not found')) {
              // Fallback: create item if update target is missing (can happen with optimistic IDs)
              savedItem = await PortfolioService.createPortfolioItem({
                id: currentItemId,
                ...portfolioItem,
                status: 'draft',
              });
              hasCreatedRef.current = true;
              setCurrentItemId(savedItem.id);
              console.log('[usePortfolioAutosave] Created item after missing update target:', savedItem.id);
            } else {
              throw updateError;
            }
          }
        } else {
          // Create new item
          if (hasCreatedRef.current) {
            // Already created, skip
            return;
          }

          savedItem = await PortfolioService.createPortfolioItem({
            ...portfolioItem,
            status: 'draft',
          });

          hasCreatedRef.current = true;
          setCurrentItemId(savedItem.id);
          console.log('[usePortfolioAutosave] Created new item:', savedItem.id);
        }

        // Update last saved data
        lastSavedDataRef.current = serializePortfolioItem(savedItem);

        // Invalidate queries to refresh data
        // Invalidate all unified portfolio queries (they're keyed by profileId and includeDrafts)
        queryClient.invalidateQueries({ queryKey: ['unified-portfolio'] });
        queryClient.invalidateQueries({ queryKey: ['unified-shots'] });
        queryClient.invalidateQueries({ queryKey: ['portfolio-items'] }); // Legacy support

        setSaveStatus('saved');
        onSaveSuccess?.(savedItem);

        // Reset to idle after 2 seconds
        setTimeout(() => {
          setSaveStatus('idle');
        }, 2000);

      } catch (error) {
        console.error('[usePortfolioAutosave] Auto-save failed:', error);
        setSaveStatus('error');
        onSaveError?.(error as Error);

        // Reset to idle after 3 seconds
        setTimeout(() => {
          setSaveStatus('idle');
        }, 3000);
      }
    }, debounceMs);

    // Cleanup timeout on unmount
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [portfolioItem, currentItemId, user, debounceMs, enabled, onSaveSuccess, onSaveError, queryClient]);

  return {
    saveStatus,
    currentItemId,
    isSaving: saveStatus === 'saving',
  };
}
